import { useCallback, useEffect, useRef, useState } from "react";
import { SrIcon } from "@/components/ui/SrIcon";
import type { SalesDisplaySlide } from "@/lib/sales-display-slides";
import {
  isSalesDisplayDraftReady,
  type SalesDisplayDraftMessage,
} from "@/lib/sales-display-preview";
import { ScaledDisplayFrame } from "./ScaledDisplayFrame";

const DRAFT_SRC = "/display/utkast";

interface Props {
  /** Utkastet i skjemaet. Null for widget-slides, som ikke kan vises som utkast. */
  draft: SalesDisplaySlide | null;
  /** Satt når skjemaet står på «Salgswidget», som hentes live og ikke kan tegnes her. */
  widgetLabel?: string | null;
}

/**
 * VIB-2705: sliden som holder på å bli skrevet, vist slik den blir på skjermen.
 *
 * Den er ikke lagret og finnes ikke i loopen, så den kan ikke vises gjennom
 * loop-previewen. Utkastet sendes i stedet inn til `/display/utkast`, som
 * gjengir det med `ConfiguredSalesDisplaySlide` — nøyaktig samme komponent
 * TV-en bruker for beskjed, bilde og video.
 */
export function SalesDisplayDraftPreview({ draft, widgetLabel }: Props) {
  const frameRef = useRef<Window | null>(null);
  const [frameReady, setFrameReady] = useState(false);

  const send = useCallback((slide: SalesDisplaySlide | null) => {
    const message: SalesDisplayDraftMessage = {
      type: "sales-display-preview/draft",
      slide,
    };
    frameRef.current?.postMessage(message, window.location.origin);
  }, []);

  // Flata sier selv fra når den lytter. Å vente på iframens `onLoad` er ikke
  // nok: dokumentet er lastet før React har montert lytteren, og et utkast
  // sendt i det vinduet blir borte uten at noe feiler.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!isSalesDisplayDraftReady(event.data)) return;
      // Svar til avsenderen, ikke til en ref vi håper er satt: `onLoad` og
      // React-monteringen i iframen kan komme i hvilken som helst rekkefølge.
      frameRef.current = event.source as Window | null;
      setFrameReady(true);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Utkastet endrer seg for hvert tastetrykk, så det sendes på nytt hver gang.
  useEffect(() => {
    if (!frameReady) return;
    send(draft);
  }, [draft, frameReady, send]);

  if (widgetLabel) {
    return (
      <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-center">
        <SrIcon name="chart-no-axes-combined" className="size-8 text-muted-foreground" />
        <p className="text-sm font-medium">{widgetLabel}</p>
        <p className="max-w-xs text-sm text-muted-foreground">
          Denne typen hentes med ekte tall når skjermen viser den. Bla til den i
          loopen over for å se hvordan den blir.
        </p>
      </div>
    );
  }

  return (
    <ScaledDisplayFrame
      src={DRAFT_SRC}
      title="Forhåndsvisning av sliden du lager"
      onReady={(frame) => {
        frameRef.current = frame.contentWindow;
      }}
    />
  );
}
